import { daily30 } from '../data/callVolume';
import { recentCalls } from '../data/recentCalls';
import { callContacts } from '../data/callContacts';
import { deriveStats, scaleMetrics } from './metrics';
import { sourceMetrics } from './sources';
import type { Agent, RecentCall, UserTrendPoint, VoiceSource, VolumePoint } from '../types/voip';

const weekLabels = ['Aug 17', 'Aug 24', 'Aug 31', 'Sep 7', 'Sep 14', 'Sep 21', 'Sep 28', 'Oct 5'];
const callTimes = ['12:08 PM', '11:41 AM', '11:02 AM', '10:26 AM', '9:51 AM'];

function noise(seed: number, i: number): number {
  const x = Math.sin(seed * 71.31 + i * 19.17) * 43758.5453;
  return x - Math.floor(x);
}

function seedOf(agent: Agent): number {
  return Number(agent.id.slice(1)) || 1;
}

function weekStats(agent: Agent, source: VoiceSource) {
  return deriveStats(sourceMetrics(scaleMetrics(agent.metrics, '7d'), source));
}

/** Last 7 days of a user's calls, split by outcome */
export function weekActivity(agent: Agent, source: VoiceSource): VolumePoint[] {
  const s = weekStats(agent, source);
  const days = daily30.slice(-7);
  const seed = seedOf(agent);
  const share = (key: 'outbound' | 'inbound' | 'missed', i: number) => {
    const total = days.reduce((sum, d) => sum + d[key], 0) || 1;
    return Math.round((s[key] * days[i][key] * (0.85 + noise(seed, i) * 0.3)) / total);
  };
  return days.map((d, i) => ({
    label: d.label,
    outbound: share('outbound', i),
    inbound: share('inbound', i),
    missed: share('missed', i),
  }));
}

/** Recorded calls for a user; falls back to a representative sample when none are on file */
export function recentCallsFor(agent: Agent, source: VoiceSource): RecentCall[] {
  const onFile = recentCalls.filter((c) => c.agentId === agent.id);
  if (onFile.length > 0) return onFile.filter((c) => c.source === source);

  const s = weekStats(agent, source);
  const seed = seedOf(agent);
  const total = s.totalCalls || 1;
  return callTimes.map((time, i) => {
    const roll = noise(seed, i + 11);
    const direction: RecentCall['direction'] =
      roll < s.missed / total ? 'missed' : roll < (s.missed + s.outbound) / total ? 'outbound' : 'inbound';
    const c = callContacts[(seed * 5 + i * 3) % callContacts.length];
    return {
      id: `${agent.id}-r${i}`,
      agentId: agent.id,
      source,
      direction,
      contact: direction === 'missed' ? 'Unknown caller' : c.contact,
      number: c.number,
      durationSeconds: direction === 'missed' ? 0 : Math.round(90 + noise(seed, i + 3) * 720),
      time,
    };
  });
}

/** Weekly history for the drawer's Trends tab; the final week matches current stats */
export function trendsFor(agent: Agent, source: VoiceSource): UserTrendPoint[] {
  const s = weekStats(agent, source);
  const seed = seedOf(agent);
  const last = weekLabels.length - 1;
  return weekLabels.map((label, w) => {
    if (w === last) {
      return { label, talkSeconds: s.talkSeconds, calls: s.totalCalls, answerRate: s.answerRate, avgSeconds: s.avgSeconds };
    }
    const tilt = 1 + (agent.trend / 100) * ((w - last) / last) * 2;
    const j = (k: number) => 0.9 + noise(seed, w * 4 + k) * 0.2;
    return {
      label,
      talkSeconds: Math.round(s.talkSeconds * tilt * j(1)),
      calls: Math.round(s.totalCalls * tilt * j(2)),
      answerRate: Math.min(1, Math.max(0.5, s.answerRate + (noise(seed, w * 4 + 3) - 0.5) * 0.06 + ((w - last) / last) * (agent.trend / 100) * 0.3)),
      avgSeconds: Math.round(s.avgSeconds * j(4)),
    };
  });
}
